import {
  Person,
  Shift,
  ShiftInput,
  ShiftWaitlistEntryGroup,
  WaitlistEntry,
  WaitlistStatus,
} from '@amzn/red-velvet-api';

/**
 * Materializes the waitlist toggle's default so a NEW shift's payload matches what the UX shows.
 * When the feature is on, a NEW CAPPED shift (maxSignup > 0) with an unset waitlistEnabled is sent
 * as `true`. "New" means the whole create flow (isCreateFlow -- every shift is being created, and
 * auto-save may stamp a shiftId mid-build) OR, in edit, a shift with no shiftId yet. An unlimited
 * new shift is left untouched (a waitlist only applies to a capped shift; cce-7844). A pre-existing
 * edit-flow shift (has shiftId, not create flow) is left untouched: an unset value predates the
 * rollout and must stay disabled. The feature-off case preserves the stored value too.
 */
export function applyWaitlistEnabledDefault(
  shifts: ShiftInput[],
  featureEnabled: boolean,
  isCreateFlow = false
): ShiftInput[] {
  if (!featureEnabled) {
    return shifts;
  }
  // Capacity-aware new-shift default: only a CAPPED shift (maxSignup > 0) defaults ON, and only when
  // it is new -- the whole create flow (isCreateFlow) or, in edit, a shift with no shiftId yet. An
  // unlimited shift has no use for a waitlist (cce-7844) and stays undefined -> disabled.
  return shifts.map((shift) =>
    shift.waitlistEnabled === undefined &&
    (isCreateFlow || !shift.shiftId) &&
    !!shift.maxSignup &&
    shift.maxSignup > 0
      ? { ...shift, waitlistEnabled: true }
      : shift
  );
}

/**
 * Returns the volunteer's full name from the PAPI-enriched waitlist entry,
 * falling back to the alias when no name is available.
 */
export function getVolunteerFullName(entry: WaitlistEntry): string {
  const person: Person | undefined = entry.waitlistUserInfo;
  const fullName = [person?.firstName, person?.lastName].filter(Boolean).join(' ').trim();
  return fullName || entry.alias;
}

/**
 * Groups waitlist entries by shiftId into the shape BulkLeaveWaitlist expects.
 */
export function groupEntriesByShift(entries: WaitlistEntry[]): ShiftWaitlistEntryGroup[] {
  const groups = new Map<string, ShiftWaitlistEntryGroup>();
  for (const entry of entries) {
    const group = groups.get(entry.shiftId);
    if (group) {
      group.waitlistEntryIds.push(entry.waitlistEntryId);
    } else {
      groups.set(entry.shiftId, {
        shiftId: entry.shiftId,
        waitlistEntryIds: [entry.waitlistEntryId],
      });
    }
  }
  return Array.from(groups.values());
}

/** Only ACTIVE and SIGNED_UP entries are shown on the coordinator Manage Waitlist page. */
export function isDisplayableWaitlistEntry(entry: WaitlistEntry): boolean {
  return entry.status === WaitlistStatus.ACTIVE || entry.status === WaitlistStatus.SIGNED_UP;
}

/** A waitlist entry flattened + enriched for the coordinator table (also the search index). */
export interface WaitlistRow {
  waitlistEntry: WaitlistEntry;
  /** 1-based rank within the entry's shift (added-time order). */
  positionInShift: number;
  shiftName: string;
  volunteerName: string;
  alias: string;
  /** Human-readable status label; used for display and free-text search. */
  status: string;
  /** Formatted added date (e.g., "Jan 21, 2026"); used for display and free-text search. */
  addedDate: string;
  /** Formatted added time with timezone (e.g., "3:45 PM PDT"); used for display. */
  addedTime: string;
  /** Epoch millis of createdTime, for sorting. */
  addedTimestamp: number;
  isActive: boolean;
}

export interface WaitlistRowFormatters {
  statusLabel: (status: WaitlistStatus) => string;
  addedDate: (createdTime: WaitlistEntry['createdTime']) => string;
  addedTime: (createdTime: WaitlistEntry['createdTime']) => string;
}

/**
 * Sorts displayable entries by shift name then added time, assigns a per-shift position,
 * and enriches each with shift name / volunteer name / searchable status + date fields.
 */
export function buildWaitlistRows(
  entries: WaitlistEntry[],
  shifts: Shift[],
  formatters: WaitlistRowFormatters
): WaitlistRow[] {
  const shiftNameById = new Map(shifts.map((shift) => [shift.shiftId, shift.name ?? '']));

  const sorted = [...entries].sort((a, b) => {
    const shiftCompare = (shiftNameById.get(a.shiftId) ?? '').localeCompare(
      shiftNameById.get(b.shiftId) ?? ''
    );
    if (shiftCompare !== 0) {
      return shiftCompare;
    }
    return new Date(a.createdTime).getTime() - new Date(b.createdTime).getTime();
  });

  const positionByShift = new Map<string, number>();
  return sorted.map((entry) => {
    const position = (positionByShift.get(entry.shiftId) ?? 0) + 1;
    positionByShift.set(entry.shiftId, position);
    return {
      waitlistEntry: entry,
      positionInShift: position,
      shiftName: shiftNameById.get(entry.shiftId) ?? '',
      volunteerName: getVolunteerFullName(entry),
      alias: entry.alias,
      status: formatters.statusLabel(entry.status),
      addedDate: formatters.addedDate(entry.createdTime),
      addedTime: formatters.addedTime(entry.createdTime),
      addedTimestamp: new Date(entry.createdTime).getTime(),
      isActive: entry.status === WaitlistStatus.ACTIVE,
    };
  });
}
